import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { all_routes } from '../../router/all_routes'
import ImageWithBasePath from '../../../core/common/imageWithBasePath'
import { getUserProfile } from '../../../api/user'

const PLACEHOLDER_IMG =
  '/assets/img/avatar/avatar1.jpg'

interface UserProfile {
  id: number
  fullname: string
  email: string
  phone_number: string
  gender: string
  dob: string | null
  agency: string | null
  nik: string | null
  address: string | null
  profession: string | null
  bio: string | null
  profile_picture: string | null
  role: string
  email_verified_at: string | null
  created_at: string
  updated_at: string
}

const ProfileCard = (props: { user?: UserProfile }) => {
  const [user, setUser] = useState<UserProfile | null>(props.user || null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!props.user) {
      // First try to get from localStorage
      try {
        const userStr = localStorage.getItem('user')
        if (userStr) {
          const parsed = JSON.parse(userStr)
          setUser(parsed)
        }
      } catch (e) {
        setUser(null)
      }

      // Then fetch fresh data from API to ensure we have the latest
      setLoading(true)
      getUserProfile()
        .then((data) => {
          setUser(data.user)
        })
        .catch((error) => {
          console.error('Failed to fetch user profile:', error)
          // Keep the localStorage data if API fails
        })
        .finally(() => {
          setLoading(false)
        })
    } else {
      setUser(props.user)
    }
  }, [props.user])

  // Listen for localStorage changes
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'user' && e.newValue && !props.user) {
        try {
          const parsed = JSON.parse(e.newValue)
          setUser(parsed)
        } catch (error) {
          console.error('Failed to parse user data from localStorage:', error)
        }
      }
    }

    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [props.user])

  return (
    <div className="profile-card overflow-hidden bg-blue-gradient2 mb-5 p-5">
      <div className="profile-card-bg">
        <ImageWithBasePath
          src="assets/img/bg/card-bg-01.png"
          className="profile-card-bg-1"
          alt=""
        />
      </div>
      <div className="row align-items-center row-gap-3">
        <div className="col-lg-6">
          <div className="d-flex align-items-center">
            <span className="avatar avatar-xxl avatar-rounded avatar-settings me-3 border border-white border-2 position-relative">
              {loading ? (
                <div className="d-flex align-items-center justify-content-center">
                  <div
                    className="spinner-border spinner-border-sm text-white"
                    role="status"
                  >
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : (
                <ImageWithBasePath
                  src=""
                  srcApi={user?.profile_picture}
                  alt=""
                  className="img-fluid rounded-circle"
                  width={80}
                  height={80}
                  fallbackSrc={PLACEHOLDER_IMG}
                />
              )}
              <span className="verify-tick">
                <i className="isax isax-verify5" />
              </span>
            </span>
            <div>
              <h5 className="mb-1 text-white d-inline-flex align-items-center">
                <Link to={all_routes.studentsDetails}>
                  {loading ? 'Loading...' : user?.fullname || 'User'}
                </Link>
                <Link
                  to={all_routes.studentProfile}
                  className="link-light fs-16 ms-2"
                >
                  <i className="isax isax-edit-2" />
                </Link>
              </h5>
              <p className="text-light">Student</p>
            </div>
          </div>
        </div>
        <div className="col-lg-6">
          {user?.role !== 'user' && (
            <div className="d-flex align-items-center justify-content-lg-end flex-wrap gap-2">
              <Link
                to={all_routes.becomeAnInstructor}
                className="btn btn-white rounded-pill me-3"
              >
                Become an Instructor
              </Link>
              <Link
                to={all_routes.instructorDashboard}
                className="btn btn-secondary rounded-pill"
              >
                Instructor Dashboard
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ProfileCard
